import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import { erpApi } from "../../../api/erp";
import { productsApi } from "../../../api/products";
import {
  CASH_REGISTER_KIND_LABELS,
  ERP_BRAND,
  cashRegisterNeedsSession,
  entityId,
  formatMoney,
  paymentMethodForCashRegister,
  projectProductCode,
  projectProductName,
  projectProductPrice,
  projectProductStock,
  safeNumber,
  toLocalDateTimeInput,
  uniqueRequestKey,
} from "../../../utils/erp";
import { hasProjectPermission } from "../../../utils/projectAccess";

const KIND_OPTIONS = [
  { value: "sale", label: "Vendí algo", detail: "Producto o servicio", permission: "erp.sales.create" },
  { value: "purchase", label: "Compré mercadería", detail: "Suma stock y costo", permission: "erp.purchases.create" },
  { value: "expense", label: "Pagué un gasto", detail: "Servicio, alquiler o insumo", permission: "erp.expenses.create" },
  { value: "collection", label: "Cobré una deuda", detail: "Un cliente me pagó", permission: "erp.collections.create" },
  { value: "payment", label: "Pagué una deuda", detail: "Le pagué a un proveedor", permission: "erp.payments.create" },
  { value: "transfer", label: "Moví dinero", detail: "Entre dos cajas o cuentas", permission: "erp.payments.create" },
  { value: "cash_in", label: "Ingresó dinero", detail: "Sin venta asociada", permission: "cash.operations.in", legacyPermission: "cash.operations.create" },
  { value: "cash_out", label: "Retiré dinero", detail: "Uso personal u otro motivo", permission: "cash.operations.out", legacyPermission: "cash.operations.create" },
  { value: "opening_balance", label: "Cargué stock que ya tenía", detail: "Inventario inicial, sin mover dinero", permission: "erp.stock.manage" },
  { value: "stock_adjustment", label: "Corregí stock", detail: "Rotura, conteo o diferencia", permission: "erp.stock.manage" },
];

const ITEM_KINDS = new Set(["sale", "purchase", "expense", "opening_balance", "stock_adjustment"]);
const COMMERCIAL_KINDS = new Set(["sale", "purchase", "expense"]);
const MANUAL_MONEY_KINDS = new Set(["collection", "payment", "cash_in", "cash_out"]);

function emptyItem() {
  return {
    productId: "",
    variantId: "",
    name: "",
    itemType: "product",
    quantity: "1",
    adjustmentDelta: "0",
    unitPrice: "0",
    unitCost: "0",
    discountAmount: "0",
    taxAmount: "0",
    tracksStock: false,
  };
}

function emptyPayment() {
  return { amount: "0", cashRegisterId: "", reference: "" };
}

function emptyForm() {
  return {
    kind: "sale",
    occurredAt: toLocalDateTimeInput(),
    description: "",
    notes: "",
    sourceNumber: "",
    partyType: "customer",
    partyName: "",
    partyDocument: "",
    partyEmail: "",
    partyPhone: "",
    items: [emptyItem()],
    payments: [],
    manualAmount: "",
    moneyRegisterId: "",
    paymentReference: "",
    transferFromId: "",
    transferToId: "",
    idempotencyKey: uniqueRequestKey("movement-ui"),
  };
}

function productVariants(product) {
  const source = product?.variants || product?.variations || [];
  return (Array.isArray(source) ? source : []).filter((variant) => variant?.active !== false);
}

function variantIdOf(variant) {
  return entityId(variant);
}

function variantLabelOf(product, variant) {
  const axes = product?.variantAttributes || product?.variantOptions || [];
  return (Array.isArray(axes) ? axes : [])
    .map((axis) => variant?.attributes?.[axis.key])
    .filter(Boolean)
    .join(" / ");
}

function variantPriceOf(product, variant) {
  const ownPrice = variant?.price;
  return ownPrice === null || ownPrice === undefined || ownPrice === ""
    ? projectProductPrice(product)
    : safeNumber(ownPrice);
}

function lineTotal(item, kind) {
  if (kind === "stock_adjustment") return 0;
  const quantity = Math.max(0, safeNumber(item.quantity));
  const unitValue = ["purchase", "expense", "opening_balance"].includes(kind)
    ? safeNumber(item.unitCost || item.unitPrice)
    : safeNumber(item.unitPrice);
  return Math.max(0, quantity * unitValue - safeNumber(item.discountAmount) + safeNumber(item.taxAmount));
}

function normalizeExisting(movement) {
  if (!movement) return emptyForm();
  const payments = (movement.payments || []).map((payment) => ({
    amount: String(payment.amount || 0),
    cashRegisterId: entityId(payment.cashRegisterId),
    reference: payment.reference || "",
  }));
  const firstPayment = payments[0] || emptyPayment();
  return {
    ...emptyForm(),
    kind: movement.kind || "sale",
    occurredAt: toLocalDateTimeInput(movement.occurredAt),
    description: movement.description || "",
    notes: movement.notes || "",
    sourceNumber: movement.sourceNumber || "",
    partyType: movement.party?.type || "other",
    partyName: movement.party?.name || "",
    partyDocument: movement.party?.document || "",
    partyEmail: movement.party?.email || "",
    partyPhone: movement.party?.phone || "",
    items: (movement.items || []).length
      ? movement.items.map((item) => ({
          productId: entityId(item.productId),
          variantId: entityId(item.variantId),
          name: item.nameSnapshot || "",
          itemType: item.itemTypeSnapshot || "product",
          quantity: String(item.quantity || 1),
          adjustmentDelta: String(item.stockDelta ?? 0),
          unitPrice: String(item.unitPrice || 0),
          unitCost: String(item.unitCost || 0),
          discountAmount: String(item.discountAmount || 0),
          taxAmount: String(item.taxAmount || 0),
          tracksStock: Boolean(item.tracksStockSnapshot),
        }))
      : [emptyItem()],
    payments: COMMERCIAL_KINDS.has(movement.kind) ? payments : [],
    manualAmount: MANUAL_MONEY_KINDS.has(movement.kind) ? String(movement.total || 0) : "",
    moneyRegisterId: MANUAL_MONEY_KINDS.has(movement.kind) ? firstPayment.cashRegisterId : "",
    paymentReference: MANUAL_MONEY_KINDS.has(movement.kind) ? firstPayment.reference : "",
    transferFromId: entityId(movement.transfer?.fromCashRegisterId),
    transferToId: entityId(movement.transfer?.toCashRegisterId),
    idempotencyKey: movement.idempotencyKey || uniqueRequestKey("movement-ui"),
  };
}

function kindAllowed(access, option) {
  return hasProjectPermission(access, option.permission)
    || (option.legacyPermission && hasProjectPermission(access, option.legacyPermission));
}

function SectionHeading({ step, title, description, action = null }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ sm: "flex-start" }}
      spacing={1.4}
      sx={{ mb: 1.8 }}
    >
      <Stack direction="row" spacing={1.2} alignItems="flex-start">
        <Box
          sx={{
            width: 30,
            height: 30,
            flex: "0 0 auto",
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: ERP_BRAND.dark,
            color: "#fff",
            fontWeight: 950,
            fontSize: 13,
            boxShadow: "0 6px 16px rgba(25,36,47,.18)",
          }}
        >
          {step}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark, lineHeight: 1.2 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.35, lineHeight: 1.45 }}>
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {action}
    </Stack>
  );
}

export default function MovementFormDialog({
  open,
  onClose,
  onSaved,
  projectId,
  movement = null,
  bootstrap = { registers: [], openSessions: [] },
  access = {},
  initialKind = "",
  initialProductId = "",
}) {
  const mobile = useMediaQuery("(max-width:599.95px)", { noSsr: true });
  const [form, setForm] = React.useState(emptyForm);
  const [products, setProducts] = React.useState([]);
  const [loadingProducts, setLoadingProducts] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const registers = React.useMemo(
    () => (bootstrap.registers || []).filter((register) => register?.active !== false),
    [bootstrap.registers],
  );
  const openRegisterIds = React.useMemo(
    () => new Set((bootstrap.openSessions || []).map((session) => entityId(session.registerId))),
    [bootstrap.openSessions],
  );
  const allowedKinds = React.useMemo(
    () => KIND_OPTIONS.filter((option) => kindAllowed(access, option)),
    [access],
  );

  React.useEffect(() => {
    if (!open) return;
    const normalized = normalizeExisting(movement);
    if (!movement) {
      const requested = allowedKinds.find((option) => option.value === initialKind);
      normalized.kind = requested?.value || allowedKinds[0]?.value || "sale";
    }
    setForm(normalized);
    setError("");
  }, [allowedKinds, initialKind, movement, open]);

  React.useEffect(() => {
    if (!open || !projectId) return undefined;
    let mounted = true;
    setLoadingProducts(true);
    productsApi
      .list(projectId)
      .then((data) => {
        if (mounted) setProducts(data.products || []);
      })
      .catch((err) => {
        if (mounted) setError(err.message || "No se pudieron cargar los ítems");
      })
      .finally(() => {
        if (mounted) setLoadingProducts(false);
      });
    return () => { mounted = false; };
  }, [open, projectId]);

  const usesItems = ITEM_KINDS.has(form.kind);
  const commercial = COMMERCIAL_KINDS.has(form.kind);
  const manualMoney = MANUAL_MONEY_KINDS.has(form.kind);
  const transferMode = form.kind === "transfer";
  const openingInventory = form.kind === "opening_balance";
  const stockAdjustment = form.kind === "stock_adjustment";
  const requiresParty = ["sale", "purchase", "expense", "collection", "payment"].includes(form.kind);
  const requiresStockProduct = openingInventory || stockAdjustment;

  const productCapabilities = React.useCallback((product) => {
    const operational = product?.operational || {};
    const kind = String(product?.erpItemKind || product?.itemKind || product?.itemType || product?.tipo || "product").toLowerCase();
    const defaults = {
      product: { sellable: true, purchasable: true, stockTracked: true, expenseEnabled: false },
      service: { sellable: true, purchasable: false, stockTracked: false, expenseEnabled: false },
      supply: { sellable: false, purchasable: true, stockTracked: true, expenseEnabled: true },
      expense: { sellable: false, purchasable: true, stockTracked: false, expenseEnabled: true },
      asset: { sellable: false, purchasable: true, stockTracked: false, expenseEnabled: true },
      charge: { sellable: true, purchasable: true, stockTracked: false, expenseEnabled: true },
    }[kind] || {};
    return { ...defaults, ...operational, kind };
  }, []);

  const selectableProducts = React.useMemo(
    () => products.filter((product) => {
      const capabilities = productCapabilities(product);
      if (form.kind === "sale") return capabilities.sellable === true;
      if (form.kind === "purchase") return capabilities.purchasable === true;
      if (form.kind === "expense") return capabilities.expenseEnabled === true;
      if (requiresStockProduct) return capabilities.stockTracked === true;
      return true;
    }),
    [form.kind, productCapabilities, products, requiresStockProduct],
  );

  const total = React.useMemo(() => {
    if (usesItems) return form.items.reduce((sum, item) => sum + lineTotal(item, form.kind), 0);
    return Math.max(0, safeNumber(form.manualAmount));
  }, [form.items, form.kind, form.manualAmount, usesItems]);
  const paid = React.useMemo(() => {
    if (manualMoney || transferMode) return total;
    return form.payments.reduce((sum, payment) => sum + safeNumber(payment.amount), 0);
  }, [form.payments, manualMoney, total, transferMode]);
  const pending = commercial ? Math.max(0, total - paid) : 0;

  const registerById = React.useCallback(
    (id) => registers.find((register) => entityId(register) === String(id || "")),
    [registers],
  );
  const registerIsUsable = React.useCallback((register) => {
    if (!register) return false;
    if (!cashRegisterNeedsSession(register)) return true;
    return openRegisterIds.has(entityId(register));
  }, [openRegisterIds]);

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateItem = React.useCallback((index, patch) => setForm((current) => ({
    ...current,
    items: current.items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)),
  })), []);
  const addItem = () => setForm((current) => ({ ...current, items: [...current.items, emptyItem()] }));
  const removeItem = (index) => setForm((current) => ({ ...current, items: current.items.filter((_, itemIndex) => itemIndex !== index) }));
  const stepAdjustmentDelta = React.useCallback((index, step) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        const currentDelta = Number(item.adjustmentDelta);
        const nextDelta = (Number.isFinite(currentDelta) ? currentDelta : 0) + step;
        return { ...item, adjustmentDelta: String(nextDelta) };
      }),
    }));
  }, []);
  const addPayment = () => setForm((current) => ({
    ...current,
    payments: [...current.payments, { ...emptyPayment(), amount: String(Math.max(0, pending)) }],
  }));
  const updatePayment = (index, patch) => setForm((current) => ({
    ...current,
    payments: current.payments.map((payment, paymentIndex) => (paymentIndex === index ? { ...payment, ...patch } : payment)),
  }));
  const removePayment = (index) => setForm((current) => ({
    ...current,
    payments: current.payments.filter((_, paymentIndex) => paymentIndex !== index),
  }));

  const selectProduct = React.useCallback((index, productId) => {
    const product = products.find((candidate) => entityId(candidate) === productId);
    if (!product) {
      updateItem(index, { productId: "", variantId: "", name: "", tracksStock: false });
      return;
    }
    const capabilities = productCapabilities(product);
    const storedCost = product?.costing?.lastCost ?? product?.costing?.averageCost ?? product?.cost ?? 0;
    const usesCost = ["purchase", "expense", "opening_balance"].includes(form.kind);
    const variants = productVariants(product);
    const automaticVariant = variants.length === 1 ? variants[0] : null;
    updateItem(index, {
      productId,
      variantId: automaticVariant ? variantIdOf(automaticVariant) : "",
      name: projectProductName(product),
      itemType: capabilities.kind,
      unitPrice: usesCost
        ? String(storedCost || projectProductPrice(product))
        : String(automaticVariant ? variantPriceOf(product, automaticVariant) : projectProductPrice(product)),
      unitCost: String(storedCost || 0),
      tracksStock: capabilities.stockTracked === true,
    });
  }, [form.kind, productCapabilities, products, updateItem]);

  React.useEffect(() => {
    if (!open || movement || !initialProductId || !products.length) return;
    if (!products.some((product) => entityId(product) === initialProductId)) return;
    selectProduct(0, initialProductId);
  }, [initialProductId, movement, open, products, selectProduct]);

  const selectVariant = (index, variantId) => {
    const item = form.items[index];
    const product = products.find((candidate) => entityId(candidate) === item?.productId);
    const variant = productVariants(product).find((candidate) => variantIdOf(candidate) === variantId);
    const usesCost = ["purchase", "expense", "opening_balance"].includes(form.kind);
    updateItem(index, {
      variantId,
      ...(!usesCost && variant ? { unitPrice: String(variantPriceOf(product, variant)) } : {}),
    });
  };

  const variantsForItem = (item) => {
    const product = products.find((candidate) => entityId(candidate) === item?.productId);
    return { product, variants: productVariants(product) };
  };

  const chooseKind = (kind) => {
    setForm((current) => ({
      ...current,
      kind,
      partyType: ["purchase", "expense", "payment"].includes(kind) ? "supplier" : "customer",
      payments: COMMERCIAL_KINDS.has(kind) ? current.payments : [],
      items: ITEM_KINDS.has(kind) ? (current.items.length ? current.items : [emptyItem()]) : current.items,
      description: current.description || KIND_OPTIONS.find((option) => option.value === kind)?.label || "",
    }));
  };

  const registerOptionLabel = (register) => {
    const kindLabel = CASH_REGISTER_KIND_LABELS[register.kind] || "Caja o cuenta";
    const sessionText = cashRegisterNeedsSession(register)
      ? (openRegisterIds.has(entityId(register)) ? " · abierta" : " · cerrada")
      : "";
    return `${register.name} · ${kindLabel}${sessionText}`;
  };

  const buildPayload = () => {
    const items = usesItems
      ? form.items.map((item) => ({
          productId: item.productId || undefined,
          variantId: item.variantId || undefined,
          name: item.name,
          itemType: item.itemType,
          quantity: stockAdjustment ? Math.abs(safeNumber(item.adjustmentDelta)) : safeNumber(item.quantity),
          stockDelta: stockAdjustment ? safeNumber(item.adjustmentDelta) : undefined,
          unitPrice: stockAdjustment
            ? 0
            : ["purchase", "expense", "opening_balance"].includes(form.kind)
              ? safeNumber(item.unitCost || item.unitPrice)
              : safeNumber(item.unitPrice),
          unitCost: stockAdjustment ? undefined : safeNumber(item.unitCost),
          discountAmount: stockAdjustment ? 0 : safeNumber(item.discountAmount),
          taxAmount: stockAdjustment ? 0 : safeNumber(item.taxAmount),
          tracksStock: Boolean(item.tracksStock),
        }))
      : [];

    let payments = [];
    if (commercial) {
      payments = form.payments
        .filter((payment) => safeNumber(payment.amount) > 0)
        .map((payment) => {
          const register = registerById(payment.cashRegisterId);
          return {
            method: paymentMethodForCashRegister(register),
            amount: safeNumber(payment.amount),
            cashRegisterId: payment.cashRegisterId,
            reference: payment.reference,
          };
        });
    } else if (manualMoney) {
      const register = registerById(form.moneyRegisterId);
      payments = [{
        method: paymentMethodForCashRegister(register),
        amount: total,
        cashRegisterId: form.moneyRegisterId,
        reference: form.paymentReference,
      }];
    }

    const selectedRegister = transferMode
      ? registerById(form.transferFromId)
      : manualMoney
        ? registerById(form.moneyRegisterId)
        : registerById(form.payments.find((payment) => safeNumber(payment.amount) > 0)?.cashRegisterId);

    return {
      kind: form.kind,
      currency: selectedRegister?.currency || "ARS",
      occurredAt: form.occurredAt ? new Date(form.occurredAt).toISOString() : undefined,
      documentDate: form.occurredAt ? new Date(form.occurredAt).toISOString() : undefined,
      description: form.description,
      notes: form.notes,
      sourceType: openingInventory ? "inventory_onboarding" : "manual_panel",
      sourceNumber: form.sourceNumber,
      idempotencyKey: form.idempotencyKey,
      total: usesItems ? undefined : total,
      party: {
        type: requiresParty ? form.partyType : "other",
        name: requiresParty ? form.partyName : "",
        document: requiresParty ? form.partyDocument : "",
        email: requiresParty ? form.partyEmail : "",
        phone: requiresParty ? form.partyPhone : "",
      },
      items,
      payments,
      ...(openingInventory ? { metadata: { counterAccountCode: "OWNER_EQUITY", openingInventory: true } } : {}),
      ...(transferMode ? {
        transfer: {
          fromCashRegisterId: form.transferFromId,
          toCashRegisterId: form.transferToId,
          amount: total,
        },
      } : {}),
    };
  };

  const validateRegister = (registerId, directionLabel) => {
    const register = registerById(registerId);
    if (!register) return `Elegí ${directionLabel}.`;
    if (!registerIsUsable(register)) return `${register.name} está cerrada. Abrila antes de usar efectivo.`;
    return "";
  };

  const validate = () => {
    if (!form.description.trim()) return "Contá brevemente qué pasó.";
    if (!allowedKinds.some((option) => option.value === form.kind)) return "No tenés permiso para registrar esta acción.";

    if (usesItems) {
      if (!form.items.length) return "Agregá al menos un ítem.";
      for (let index = 0; index < form.items.length; index += 1) {
        const item = form.items[index];
        if (!item.name.trim()) return `Completá el ítem ${index + 1}.`;
        if (requiresStockProduct && !item.productId) return `Elegí un producto para la línea ${index + 1}.`;
        const { variants } = variantsForItem(item);
        if (item.productId && variants.length && !item.variantId) return `Elegí la variante de la línea ${index + 1}.`;
        if (stockAdjustment && safeNumber(item.adjustmentDelta) === 0) return `La corrección de la línea ${index + 1} no puede ser cero.`;
        if (!stockAdjustment && safeNumber(item.quantity) <= 0) return `La cantidad de la línea ${index + 1} debe ser mayor a cero.`;
        if (["sale", "purchase", "expense", "opening_balance"].includes(form.kind)) {
          const unitValue = ["purchase", "expense", "opening_balance"].includes(form.kind)
            ? safeNumber(item.unitCost || item.unitPrice)
            : safeNumber(item.unitPrice);
          if (unitValue <= 0) return `Completá el importe del ítem ${index + 1}.`;
        }
      }
    }

    if ((manualMoney || transferMode) && total <= 0) return "Indicá un importe mayor a cero.";
    if (commercial && paid > total) return "El dinero registrado no puede superar el total.";
    if (commercial) {
      const paymentCurrencies = new Set(
        form.payments
          .filter((payment) => safeNumber(payment.amount) > 0)
          .map((payment) => registerById(payment.cashRegisterId)?.currency)
          .filter(Boolean),
      );
      if (paymentCurrencies.size > 1) {
        return "Para cobrar o pagar en monedas distintas, registrá cada moneda como una operación separada.";
      }
    }

    for (const payment of form.payments) {
      if (safeNumber(payment.amount) <= 0) continue;
      const registerError = validateRegister(payment.cashRegisterId, "dónde entró o salió el dinero");
      if (registerError) return registerError;
    }
    if (manualMoney) {
      const registerError = validateRegister(
        form.moneyRegisterId,
        ["collection", "cash_in"].includes(form.kind) ? "dónde ingresó el dinero" : "de dónde salió el dinero",
      );
      if (registerError) return registerError;
    }
    if (transferMode) {
      const fromError = validateRegister(form.transferFromId, "la caja o cuenta de origen");
      if (fromError) return fromError;
      const toError = validateRegister(form.transferToId, "la caja o cuenta de destino");
      if (toError) return toError;
      if (form.transferFromId === form.transferToId) return "El origen y el destino deben ser distintos.";
      const from = registerById(form.transferFromId);
      const to = registerById(form.transferToId);
      if (from?.currency !== to?.currency) return "Las transferencias entre monedas distintas se registran como una salida y un ingreso.";
    }
    return "";
  };

  const submit = async (confirmNow) => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = buildPayload();
      const movementId = entityId(movement);
      let saved;
      if (movementId) {
        const updated = await erpApi.updateMovement(projectId, movementId, payload);
        saved = updated.movement;
        if (confirmNow) {
          const confirmed = await erpApi.confirmMovement(projectId, movementId);
          saved = confirmed.movement;
        }
      } else if (confirmNow) {
        const response = await erpApi.createConfirmedMovement(projectId, payload);
        saved = response.movement;
      } else {
        const response = await erpApi.createMovement(projectId, payload);
        saved = response.movement;
      }
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      setError(err.message || "No se pudo registrar lo que pasó");
    } finally {
      setSaving(false);
    }
  };

  const showMoneySetupWarning = !registers.length && (commercial || manualMoney || transferMode);
  const selectedKind = KIND_OPTIONS.find((option) => option.value === form.kind);
  const confirmationImpact = form.kind === "purchase"
    ? "Al confirmar, la compra suma stock, registra el costo y mueve dinero sólo por los pagos cargados."
    : form.kind === "sale"
      ? "Al confirmar, la venta descuenta stock y registra el ingreso o el saldo pendiente."
      : openingInventory
        ? "Al confirmar, se carga el inventario inicial sin sacar dinero de ninguna caja."
        : stockAdjustment
          ? "Al confirmar, se corrige la existencia y queda trazabilidad de la diferencia."
          : "El impacto en dinero y registros internos ocurre recién cuando confirmás.";
  const sectionSx = {
    p: { xs: 1.5, sm: 2.2 },
    borderRadius: { xs: 2.5, sm: 3 },
    border: `1px solid ${ERP_BRAND.border}`,
    borderTop: `3px solid ${ERP_BRAND.orange}`,
    bgcolor: "#fff",
    boxShadow: "0 12px 34px rgba(25,36,47,.055)",
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={mobile}
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          minHeight: mobile ? "100dvh" : "min(760px, calc(100dvh - 48px))",
          maxHeight: mobile ? "100dvh" : "calc(100dvh - 48px)",
          borderRadius: mobile ? 0 : 4,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          position: "relative",
          px: { xs: 2, sm: 3 },
          py: { xs: 1.8, sm: 2.4 },
          pr: { xs: 6, sm: 7 },
          color: "#fff",
          background: "linear-gradient(135deg,#19242F 0%,#2E4658 72%,#3C5A70 100%)",
          overflow: "hidden",
          "&::after": {
            content: '\"\"',
            position: "absolute",
            width: 150,
            height: 150,
            right: -55,
            bottom: -95,
            borderRadius: "50%",
            bgcolor: "rgba(243,90,58,.26)",
          },
        }}
      >
        <Typography variant="overline" sx={{ fontWeight: 950, letterSpacing: ".12em", color: "#FFAA96" }}>
          OPERACIÓN DEL NEGOCIO
        </Typography>
        <Typography variant={mobile ? "h6" : "h5"} sx={{ fontWeight: 950, lineHeight: 1.15 }}>
          {movement ? "Editar antes de confirmar" : "Registrar lo que pasó"}
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,.72)", mt: 0.55, maxWidth: 760 }}>
          Seguí el recorrido: elegí el caso, completá los datos y revisá el impacto antes de confirmar.
        </Typography>
        <IconButton
          onClick={onClose}
          disabled={saving}
          aria-label="Cerrar formulario"
          sx={{ position: "absolute", right: 10, top: 10, color: "#fff", zIndex: 1 }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
          bgcolor: "#F5F7FA",
          p: { xs: 1.4, sm: 2.5 },
          scrollbarWidth: "thin",
          scrollbarColor: `${ERP_BRAND.orange} rgba(25,36,47,.10)`,
          "&::-webkit-scrollbar": { width: 10 },
          "&::-webkit-scrollbar-track": { bgcolor: "rgba(25,36,47,.07)" },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: ERP_BRAND.orange,
            borderRadius: 999,
            border: "2px solid #F5F7FA",
          },
        }}
      >
        <Stack spacing={{ xs: 1.7, sm: 2.3 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.4, sm: 1.8 },
              borderRadius: 3,
              border: "1px solid rgba(25,36,47,.09)",
              bgcolor: "#fff",
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap>
              {[
                ["1", "Caso", selectedKind?.label || "Elegí qué pasó"],
                ["2", "Datos", usesItems ? "Ítems, cantidades y personas" : "Importe, origen y destino"],
                ["3", "Confirmación", "Recién acá impacta el sistema"],
              ].map(([number, label, detail]) => (
                <Box
                  key={number}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    p: 1.15,
                    borderRadius: 2.2,
                    bgcolor: number === "1" ? "rgba(243,90,58,.08)" : "rgba(25,36,47,.035)",
                    border: number === "1" ? "1px solid rgba(243,90,58,.22)" : "1px solid rgba(25,36,47,.06)",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={number}
                      sx={{ minWidth: 28, fontWeight: 950, bgcolor: number === "1" ? ERP_BRAND.orange : ERP_BRAND.dark, color: "#fff" }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ display: "block", fontWeight: 950, color: ERP_BRAND.dark }}>
                        {label}
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", color: ERP_BRAND.muted, overflowWrap: "anywhere" }}>
                        {detail}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Paper>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {showMoneySetupWarning ? (
            <Alert
              severity="warning"
              action={(
                <Button color="inherit" href={`/dashboard/proyectos/${projectId}/sistema/caja`}>
                  Crear cajas
                </Button>
              )}
            >
              Antes de mover dinero, creá las cajas y cuentas reales que usa tu negocio.
            </Alert>
          ) : null}

          <Paper elevation={0} sx={sectionSx}>
            <SectionHeading
              step="1"
              title="¿Qué tipo de operación fue?"
              description="Elegí la frase más parecida a lo que ocurrió. El formulario se adapta sin cambiar los datos ya cargados."
            />
            <Grid container spacing={1.1}>
              {allowedKinds.map((option) => {
                const selected = form.kind === option.value;
                return (
                  <Grid item xs={12} sm={6} md={4} key={option.value}>
                    <Paper
                      component="button"
                      type="button"
                      onClick={() => chooseKind(option.value)}
                      elevation={0}
                      aria-pressed={selected}
                      sx={{
                        width: "100%",
                        minHeight: { xs: 70, sm: 78 },
                        p: 1.45,
                        textAlign: "left",
                        cursor: "pointer",
                        borderRadius: 2.5,
                        border: `2px solid ${selected ? ERP_BRAND.orange : "rgba(25,36,47,.10)"}`,
                        bgcolor: selected ? "rgba(243,90,58,.09)" : "#fff",
                        boxShadow: selected ? "0 9px 22px rgba(243,90,58,.12)" : "none",
                        transition: "transform .15s ease, border-color .15s ease, box-shadow .15s ease",
                        "&:hover": { transform: "translateY(-1px)", borderColor: selected ? ERP_BRAND.orange : "rgba(25,36,47,.28)" },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Box>
                          <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark, lineHeight: 1.2 }}>{option.label}</Typography>
                          <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{option.detail}</Typography>
                        </Box>
                        {selected ? <TaskAltRoundedIcon sx={{ color: ERP_BRAND.orange, fontSize: 22 }} /> : null}
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>

          <Paper elevation={0} sx={sectionSx}>
            <SectionHeading
              step="2"
              title="Datos principales"
              description="Poné una descripción reconocible, la fecha real y un comprobante si existe."
            />
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="¿Qué pasó exactamente?"
                  placeholder="Ej.: Compra semanal a proveedor, venta de mostrador..."
                  value={form.description}
                  onChange={(event) => updateField("description", event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha y hora"
                  type="datetime-local"
                  value={form.occurredAt}
                  onChange={(event) => updateField("occurredAt", event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth label="Comprobante o referencia" value={form.sourceNumber} onChange={(event) => updateField("sourceNumber", event.target.value)} />
              </Grid>
            </Grid>
          </Paper>

          {requiresParty ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step="3"
                title={["sale", "collection"].includes(form.kind) ? "Cliente relacionado" : "Proveedor o persona relacionada"}
                description="Es opcional, pero ayuda a reconocer deudas, pagos y el historial de cada persona."
              />
              <Grid container spacing={1.4}>
                <Grid item xs={12} md={4}><TextField fullWidth label="Nombre" value={form.partyName} onChange={(event) => updateField("partyName", event.target.value)} /></Grid>
                <Grid item xs={12} sm={6} md={2.5}><TextField fullWidth label="Documento" value={form.partyDocument} onChange={(event) => updateField("partyDocument", event.target.value)} /></Grid>
                <Grid item xs={12} sm={6} md={3}><TextField fullWidth label="Email" value={form.partyEmail} onChange={(event) => updateField("partyEmail", event.target.value)} /></Grid>
                <Grid item xs={12} md={2.5}><TextField fullWidth label="Teléfono" value={form.partyPhone} onChange={(event) => updateField("partyPhone", event.target.value)} /></Grid>
              </Grid>
            </Paper>
          ) : null}

          {usesItems ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step={requiresParty ? "4" : "3"}
                title="Ítems y cantidades"
                description={form.kind === "purchase"
                  ? "Elegí el ítem que ya creaste. La compra confirmada suma la cantidad al stock y actualiza su costo."
                  : "El stock sólo cambia por esta operación; crear el ítem no inventa existencias."}
                action={(
                  <Button
                    startIcon={<AddRoundedIcon />}
                    onClick={addItem}
                    variant="outlined"
                    sx={{ textTransform: "none", fontWeight: 900, alignSelf: { xs: "stretch", sm: "auto" } }}
                  >
                    Agregar ítem
                  </Button>
                )}
              />
              {form.kind === "purchase" ? (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  Flujo recomendado: primero creá el ítem; después registrá esta compra. Al confirmar, WebProfit suma existencias sin permitir editar el stock a mano.
                </Alert>
              ) : null}
              <Stack spacing={1.3}>
                {form.items.map((item, index) => {
                  const variantData = variantsForItem(item);
                  return (
                    <Box
                      key={`${index}-${item.productId}`}
                      sx={{
                        p: { xs: 1.25, sm: 1.7 },
                        borderRadius: 2.5,
                        bgcolor: ERP_BRAND.soft,
                        border: "1px solid rgba(25,36,47,.06)",
                      }}
                    >
                      <Grid container spacing={1.2} alignItems="center">
                        <Grid item xs={12} md={3.5}>
                          <TextField select fullWidth label="Ítem" value={item.productId} onChange={(event) => selectProduct(index, event.target.value)} disabled={loadingProducts}>
                            {!requiresStockProduct ? <MenuItem value="">Concepto manual</MenuItem> : null}
                            {selectableProducts.map((product) => (
                              <MenuItem key={entityId(product)} value={entityId(product)}>
                                {projectProductName(product)}{projectProductCode(product) ? ` · ${projectProductCode(product)}` : ""}{productCapabilities(product).stockTracked ? ` · stock ${projectProductStock(product)}` : ""}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        {variantData.variants.length ? (
                          <Grid item xs={12} md={2.6}>
                            <TextField select fullWidth required label="Variante" value={item.variantId || ""} onChange={(event) => selectVariant(index, event.target.value)}>
                              <MenuItem value="">Elegí una variante</MenuItem>
                              {variantData.variants.map((variant) => (
                                <MenuItem key={variantIdOf(variant)} value={variantIdOf(variant)}>
                                  {variantLabelOf(variantData.product, variant) || variant.sku || "Variante"} · stock {safeNumber(variant.stock)}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        ) : null}
                        <Grid item xs={12} md={stockAdjustment ? 2 : 2.5}><TextField fullWidth label="Concepto" value={item.name} disabled={Boolean(item.productId)} onChange={(event) => updateItem(index, { name: event.target.value })} /></Grid>
                        <Grid item xs={12} md={stockAdjustment ? 2.6 : 1.3}>
                          {stockAdjustment ? (
                            <Stack spacing={0.35}>
                              <Stack direction="row" spacing={0.65} alignItems="center">
                                <Button
                                  type="button"
                                  variant="outlined"
                                  aria-label="Restar una unidad a la corrección"
                                  onClick={() => stepAdjustmentDelta(index, -1)}
                                  sx={{ minWidth: 50, height: 56, px: 1, fontWeight: 950 }}
                                >
                                  -1
                                </Button>
                                <TextField
                                  fullWidth
                                  type="number"
                                  label="Variación"
                                  value={item.adjustmentDelta}
                                  onChange={(event) => updateItem(index, { adjustmentDelta: event.target.value })}
                                />
                                <Button
                                  type="button"
                                  variant="outlined"
                                  aria-label="Sumar una unidad a la corrección"
                                  onClick={() => stepAdjustmentDelta(index, 1)}
                                  sx={{ minWidth: 50, height: 56, px: 1, fontWeight: 950 }}
                                >
                                  +1
                                </Button>
                              </Stack>
                              <Typography variant="caption" sx={{ color: ERP_BRAND.muted, pl: 0.25 }}>
                                + suma stock / - resta stock
                              </Typography>
                            </Stack>
                          ) : (
                            <TextField
                              fullWidth
                              type="number"
                              label="Cantidad"
                              value={item.quantity}
                              onChange={(event) => updateItem(index, { quantity: event.target.value })}
                            />
                          )}
                        </Grid>
                        {!stockAdjustment ? (
                          <Grid item xs={6} md={1.5}>
                            <TextField
                              fullWidth
                              type="number"
                              label={form.kind === "sale" ? "Precio" : "Costo"}
                              value={form.kind === "sale" ? item.unitPrice : item.unitCost}
                              onChange={(event) => updateItem(index, form.kind === "sale" ? { unitPrice: event.target.value } : { unitCost: event.target.value })}
                            />
                          </Grid>
                        ) : null}
                        {!stockAdjustment ? (
                          <Grid item xs={10} md={1.3}>
                            <Box sx={{ px: { xs: 0.5, md: 0 } }}>
                              <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Subtotal</Typography>
                              <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>{formatMoney(lineTotal(item, form.kind))}</Typography>
                            </Box>
                          </Grid>
                        ) : null}
                        <Grid item xs={2} md="auto"><IconButton color="error" aria-label="Quitar ítem" disabled={form.items.length === 1} onClick={() => removeItem(index)}><DeleteOutlineRoundedIcon /></IconButton></Grid>
                      </Grid>
                    </Box>
                  );
                })}
              </Stack>
              {openingInventory ? (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  Esta acción deja asentado el stock que ya existía antes de usar WebProfit. No descuenta dinero de ninguna caja.
                </Alert>
              ) : null}
            </Paper>
          ) : null}

          {commercial ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step={usesItems ? (requiresParty ? "5" : "4") : (requiresParty ? "4" : "3")}
                title={form.kind === "sale" ? "Cobro y caja" : "Pago y caja"}
                description={form.kind === "sale" ? "Indicá dónde recibiste el dinero. Lo que falte queda por cobrar." : "Indicá de dónde salió el dinero. Lo que falte queda por pagar."}
                action={(
                  <Button
                    startIcon={<AddRoundedIcon />}
                    onClick={addPayment}
                    disabled={!registers.length}
                    variant="outlined"
                    sx={{ textTransform: "none", fontWeight: 900, alignSelf: { xs: "stretch", sm: "auto" } }}
                  >
                    Agregar pago
                  </Button>
                )}
              />
              <Stack spacing={1.2}>
                {form.payments.map((payment, index) => (
                  <Box key={index} sx={{ p: { xs: 1.2, sm: 1.5 }, borderRadius: 2.5, bgcolor: ERP_BRAND.soft, border: "1px solid rgba(25,36,47,.06)" }}>
                    <Grid container spacing={1.2} alignItems="center">
                      <Grid item xs={12} md={5}>
                        <TextField select fullWidth label={form.kind === "sale" ? "Caja o cuenta que recibió" : "Caja o cuenta que pagó"} value={payment.cashRegisterId} onChange={(event) => updatePayment(index, { cashRegisterId: event.target.value })}>
                          {registers.map((register) => <MenuItem key={entityId(register)} value={entityId(register)} disabled={!registerIsUsable(register)}>{registerOptionLabel(register)}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={6} md={2}><TextField fullWidth type="number" label="Importe" value={payment.amount} onChange={(event) => updatePayment(index, { amount: event.target.value })} /></Grid>
                      <Grid item xs={10} md={4}><TextField fullWidth label="Referencia" value={payment.reference} onChange={(event) => updatePayment(index, { reference: event.target.value })} /></Grid>
                      <Grid item xs={2} md={1}><IconButton color="error" aria-label="Quitar pago" onClick={() => removePayment(index)}><DeleteOutlineRoundedIcon /></IconButton></Grid>
                    </Grid>
                  </Box>
                ))}
                {!form.payments.length ? <Alert severity="info">Sin pago registrado: el total quedará pendiente.</Alert> : null}
              </Stack>
            </Paper>
          ) : null}

          {manualMoney ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step={requiresParty ? "4" : "3"}
                title="Importe y caja"
                description="Seleccioná la caja o cuenta real para que el movimiento quede correctamente ubicado."
              />
              <Grid container spacing={1.4}>
                <Grid item xs={12} sm={5} md={4}><TextField fullWidth type="number" label="Importe" value={form.manualAmount} onChange={(event) => updateField("manualAmount", event.target.value)} /></Grid>
                <Grid item xs={12} sm={7} md={5}>
                  <TextField select fullWidth label={["collection", "cash_in"].includes(form.kind) ? "¿Dónde ingresó?" : "¿De dónde salió?"} value={form.moneyRegisterId} onChange={(event) => updateField("moneyRegisterId", event.target.value)}>
                    {registers.map((register) => <MenuItem key={entityId(register)} value={entityId(register)} disabled={!registerIsUsable(register)}>{registerOptionLabel(register)}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="Referencia" value={form.paymentReference} onChange={(event) => updateField("paymentReference", event.target.value)} /></Grid>
              </Grid>
            </Paper>
          ) : null}

          {transferMode ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step="3"
                title="Origen, destino e importe"
                description="El dinero sale de una caja o cuenta y entra en otra. Ambas deben usar la misma moneda."
              />
              <Grid container spacing={1.4}>
                <Grid item xs={12} md={3}><TextField fullWidth type="number" label="Importe" value={form.manualAmount} onChange={(event) => updateField("manualAmount", event.target.value)} /></Grid>
                <Grid item xs={12} sm={6} md={4.5}>
                  <TextField select fullWidth label="Sale de" value={form.transferFromId} onChange={(event) => updateField("transferFromId", event.target.value)}>
                    {registers.map((register) => <MenuItem key={entityId(register)} value={entityId(register)} disabled={!registerIsUsable(register)}>{registerOptionLabel(register)}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4.5}>
                  <TextField select fullWidth label="Entra en" value={form.transferToId} onChange={(event) => updateField("transferToId", event.target.value)}>
                    {registers.map((register) => <MenuItem key={entityId(register)} value={entityId(register)} disabled={!registerIsUsable(register)}>{registerOptionLabel(register)}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>
            </Paper>
          ) : null}

          <Paper elevation={0} sx={sectionSx}>
            <SectionHeading
              step="✓"
              title="Revisión final"
              description={confirmationImpact}
            />
            <TextField fullWidth multiline minRows={2} label="Notas opcionales" value={form.notes} onChange={(event) => updateField("notes", event.target.value)} />

            <Box
              sx={{
                mt: 1.7,
                p: { xs: 1.5, sm: 2.2 },
                color: "#fff",
                borderRadius: 3,
                background: "linear-gradient(135deg,#19242F 0%,#30495B 100%)",
              }}
            >
              <Grid container spacing={{ xs: 1.2, sm: 2 }}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.62)", fontWeight: 850 }}>TOTAL</Typography>
                  <Typography variant={mobile ? "h6" : "h5"} sx={{ fontWeight: 950 }}>{formatMoney(total)}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.62)", fontWeight: 850 }}>REGISTRADO</Typography>
                  <Typography variant={mobile ? "h6" : "h5"} sx={{ fontWeight: 950 }}>{formatMoney(paid)}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.62)", fontWeight: 850 }}>PENDIENTE</Typography>
                  <Typography variant={mobile ? "h6" : "h5"} sx={{ fontWeight: 950 }}>{formatMoney(pending)}</Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          flex: "0 0 auto",
          p: { xs: 1.2, sm: 2 },
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "flex-end",
          bgcolor: "#fff",
          borderTop: "1px solid rgba(25,36,47,.10)",
          boxShadow: "0 -10px 28px rgba(25,36,47,.06)",
          "& .MuiButton-root": { minHeight: 42 },
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          fullWidth={mobile}
          sx={{ textTransform: "none", fontWeight: 850, order: mobile ? 3 : 1 }}
        >
          Cancelar
        </Button>
        <Button
          variant="outlined"
          startIcon={<SaveRoundedIcon />}
          disabled={saving}
          onClick={() => submit(false)}
          fullWidth={mobile}
          sx={{ textTransform: "none", fontWeight: 900, order: mobile ? 2 : 2 }}
        >
          Guardar para después
        </Button>
        <Button
          variant="contained"
          startIcon={<TaskAltRoundedIcon />}
          disabled={saving || showMoneySetupWarning}
          onClick={() => submit(true)}
          fullWidth={mobile}
          sx={{
            textTransform: "none",
            fontWeight: 950,
            bgcolor: ERP_BRAND.orange,
            order: mobile ? 1 : 3,
            "&:hover": { bgcolor: "#D9472B" },
          }}
        >
          {saving ? "Registrando..." : "Registrar ahora"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
